import { Component, computed, ElementRef, input, signal, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ReactWrapperComponent } from '@examdojo/core/react-wrapper';
import { TiptapEditorFormControlDirective, TiptapEditorOutputFormat } from '@examdojo/core/tiptap';
import { NotionEditor } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-templates/notion-like/notion-like-editor';
import type {
  SlashMenuConfig,
  SlashMenuItem,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/slash-dropdown-menu/use-slash-dropdown-menu';
import type { TiptapSmartLinkConfig } from '@examdojo/core/tiptap-react-ui-components/contexts/smart-link-context';
import { TableOfContentData } from '@tiptap-pro/extension-table-of-contents';
import type { Editor, HTMLContent } from '@tiptap/core';
import { JSONContent } from '@tiptap/react';
import { createElement, ReactElement } from 'react';

@Component({
  selector: 'dojo-tiptap-editor-wrapper',
  standalone: true,
  imports: [TiptapEditorFormControlDirective, ReactiveFormsModule],
  template: `
    <div
      #notionEditorContainer
      class="h-full w-full"
      tiptap
      [editor]="editor()"
      [formControl]="valueControl()"
      [outputFormat]="outputFormat()"
    ></div>
  `,
})
export class TiptapEditorWrapperComponent<
  T extends TiptapEditorOutputFormat = TiptapEditorOutputFormat,
> extends ReactWrapperComponent {
  readonly mountElementRef = viewChild.required<ElementRef>('notionEditorContainer');
  readonly roomId = input<string>();
  readonly placeholder = input<string>();
  readonly editorOptions = input<Partial<Editor['options']>>();
  readonly outputFormat = input.required<T>();
  readonly valueControl =
    input.required<FormControl<T extends TiptapEditorOutputFormat.Html ? HTMLContent : JSONContent>>();
  readonly editable = input<boolean>(true);
  readonly scrollableParent = input<HTMLElement>();
  readonly slashMenuItems = input<Record<string, SlashMenuItem>>();
  readonly contentPadding = input<string>();
  readonly smartLinkConfig = input<TiptapSmartLinkConfig>();

  readonly editor = signal<Editor | null>(null);
  readonly tableOfContentData = signal<TableOfContentData>([]);

  readonly editor$ = toObservable(this.editor);
  readonly tableOfContentData$ = toObservable(this.tableOfContentData);

  readonly slashMenuConfig = computed<SlashMenuConfig | undefined>(() => {
    const items = this.slashMenuItems();
    if (!items || Object.keys(items).length === 0) {
      return undefined;
    }

    const itemsMetadata: Record<string, SlashMenuItem['metadata']> = {};
    const itemImplementations: Record<string, SlashMenuItem['implementation']> = {};

    Object.entries(items).forEach(([key, item]) => {
      itemsMetadata[key] = item.metadata;
      itemImplementations[key] = item.implementation;
    });

    return {
      itemsMetadata,
      itemImplementations,
    };
  });

  protected readonly reRenderTriggers = [
    this.roomId,
    this.placeholder,
    this.editorOptions,
    this.editable,
    this.scrollableParent,
    this.slashMenuItems,
    this.contentPadding,
    this.smartLinkConfig,
  ];

  protected renderReactElement(): ReactElement {
    return createElement(NotionEditor, {
      room: this.roomId(),
      placeholder: this.placeholder(),
      editorOptions: this.editorOptions(),
      editable: this.editable(),
      slashMenuConfig: this.slashMenuConfig(),
      contentPadding: this.contentPadding(),
      smartLinkConfig: this.smartLinkConfig(),
      onEditorReady: (editor: Editor) => {
        this.editor.set(editor);
      },
      tableOfContent: {
        onUpdate: (content: TableOfContentData) => {
          this.tableOfContentData.set(content);
        },
        scrollableParent: this.scrollableParent(),
      },
    });
  }
}
